import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getToken, onMessage } from 'firebase/messaging';
import { getAuth } from 'firebase/auth';
import { getMessagingInstance } from '../firebase';
import { 
  checkNotificationEligibility, 
  saveNotificationPreference,
  canRequestNotificationPermission 
} from '../services/userRegistrationService';
import usePWAInstall from '../hooks/usePWAInstall';

/**
 * Enhanced NotificationPrompt Component
 * 
 * This component displays a strategic notification permission request that only appears
 * when the user meets specific criteria:
 * 1. Has successfully installed the PWA app
 * 2. Has successfully registered for at least one event
 * 
 * Key Features:
 * - Intelligent eligibility checking based on user behavior
 * - Attention-grabbing design with clear value proposition
 * - Respectful timing with cooldown periods
 * - Firebase Cloud Messaging integration via Cloudflare
 * - Professional UI with clear messaging about notification usage
 * - Persistent preference storage to avoid repeated prompts
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Override to force show the prompt (for testing)
 * @param {Function} props.onClose - Callback function when the prompt is closed
 * @param {string} props.userId - Firebase user ID for eligibility checking
 * @returns {JSX.Element|null} The notification prompt or null if not shown
 */
const NotificationPrompt = ({ show = false, onClose, userId }) => {
  // State management for notification prompt
  const [isEligible, setIsEligible] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  
  // PWA installation status
  const { isInstalled } = usePWAInstall();
  const auth = getAuth();

  /**
   * Check if user is eligible for notification permission request
   * Runs comprehensive eligibility check based on requirements
   */
  useEffect(() => {
    const checkEligibility = async () => {
      setLoading(true);
      
      try {
        // Get current user if not provided
        const currentUser = userId || auth.currentUser?.uid;
        
        if (!currentUser) {
          setIsEligible(false);
          setLoading(false);
          return;
        }

        // Check if user meets all criteria for notification permission
        const eligibility = await checkNotificationEligibility(currentUser, isInstalled);
        
        // Check if enough time has passed since last request (respectful timing)
        const canRequest = await canRequestNotificationPermission(currentUser);
        
        // User is eligible only if they meet all criteria and cooldown has passed
        const eligible = eligibility.eligible && canRequest;
        setIsEligible(eligible);
        
        // Show prompt if eligible or if explicitly requested via props
        if (eligible || (show && !permissionGranted)) {
          setShowPrompt(true);
        }
        
      } catch (error) {
        console.error('Error checking notification eligibility:', error);
        setIsEligible(false);
      } finally {
        setLoading(false);
      }
    };

    // Check eligibility when component mounts or dependencies change
    if (isInstalled !== null) { // Wait for PWA status to be determined
      checkEligibility();
    }
  }, [userId, isInstalled, show, auth.currentUser?.uid, permissionGranted]);

  /**
   * Handle user agreeing to notification permissions
   * Requests system permission and saves FCM token
   */
  const handleAllowNotifications = async () => {
    setIsPrompting(true);
    
    try {
      // Request notification permission from the browser
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setPermissionGranted(true);
        console.log('Notification permission granted by user');

        // Get Firebase messaging instance
        const messaging = getMessagingInstance();
        
        if (messaging) {
          try {
            // Get FCM token for push notifications
            const currentToken = await getToken(messaging, { 
              vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY 
            });

            // IMPORTANT: When sending this token to your backend (Cloudflare Worker),
            // ensure you also send the `currentUser` (userId) along with it.
            // This is crucial for the backend to be able to identify and remove
            // invalid tokens from Firestore if a notification send fails.
            // Example: { userId: currentUser, fcmToken: currentToken }

            if (currentToken) {
              console.log('FCM registration token obtained:', currentToken);
              
              // Save notification preferences to Firestore
              const currentUser = userId || auth.currentUser?.uid;
              if (currentUser) {
                await saveNotificationPreference(currentUser, 'granted', currentToken);
                console.log('Notification preferences saved successfully');
              }
              
              // Set up foreground message listener
              onMessage(messaging, (payload) => {
                console.log('Foreground message received:', payload);
                
                // Create notification for foreground messages
                if (payload.notification) {
                  new Notification(payload.notification.title, {
                    body: payload.notification.body,
                    icon: payload.notification.icon || '/logo192.png',
                    tag: 'yxc-events'
                  });
                }
              });
              
            } else {
              console.warn('No FCM registration token available');
            }
          } catch (tokenError) {
            console.error('Error getting FCM token:', tokenError);
          }
        } else {
          console.warn('Firebase messaging not available');
        }
      } else {
        console.log('Notification permission denied by user');
        
        // Save the denial to avoid asking again
        const currentUser = userId || auth.currentUser?.uid;
        if (currentUser) {
          await saveNotificationPreference(currentUser, permission);
        }
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    } finally {
      setIsPrompting(false);
      setShowPrompt(false);
      onClose?.(); // Close the prompt after user interaction
    }
  };
  
  /**
   * Handle user declining notification permissions
   * Saves preference to avoid repeated prompts
   */
  const handleDeclineNotifications = async () => {
    setIsPrompting(false);
    setShowPrompt(false);
    
    try {
      // Save the user's preference to decline
      const currentUser = userId || auth.currentUser?.uid;
      if (currentUser) {
        await saveNotificationPreference(currentUser, 'denied');
        console.log('User declined notifications - preference saved');
      }
    } catch (error) {
      console.error('Error saving notification decline:', error);
    }
    
    onClose?.(); // Close the prompt
  };
  
  // Don't render if loading, not eligible, or already shown
  if (loading || (!isEligible && !show) || permissionGranted || Notification.permission === 'granted') {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
        >
          <motion.div
            initial={{ y: -50, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full text-center relative overflow-hidden"
          >
            {/* Decorative background */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-500 to-purple-600"></div>
            
            {/* Notification icon */}
            <div className="mb-4">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
            </div>
            
            {/* Heading */}
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              🎉 Stay in the Loop!
            </h3>
            
            {/* Subheading */}
            <p className="text-indigo-600 dark:text-indigo-400 font-medium mb-4">
              Get notified about your events
            </p>
            
            {/* Main message */}
            <div className="text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <p className="leading-relaxed">
                You've successfully registered for events and installed our app! 
                Allow notifications to receive important updates about:
              </p>
              
              <ul className="text-sm space-y-1 text-left max-w-sm mx-auto">
                <li className="flex items-center">
                  <span className="text-indigo-500 mr-2">•</span>
                  Event reminders and schedule changes
                </li>
                <li className="flex items-center">
                  <span className="text-indigo-500 mr-2">•</span>
                  Registration status updates
                </li>
                <li className="flex items-center">
                  <span className="text-indigo-500 mr-2">•</span>
                  Important announcements
                </li>
              </ul>
              
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                We promise not to spam you! Only essential event-related updates.
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDeclineNotifications}
                disabled={isPrompting}
                className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Not Now
              </button>
              <button
                onClick={handleAllowNotifications}
                disabled={isPrompting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none flex items-center justify-center"
              >
                {isPrompting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Enabling...
                  </>
                ) : (
                  <>
                    <span className="mr-2">🔔</span>
                    Allow Notifications
                  </>
                )}
              </button>
            </div>
            
            {/* Privacy note */}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              You can change this preference anytime in your browser settings
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationPrompt;
